import {EventEmitter} from 'events';
import {TTrack} from '../../../../typings/todo';
import {IPlayer} from '../IPlayer';

type TInterceptor<T = string> = (w: T) => string;

/**
 * Used to seamlessly switch different implementations of player
 */
export class PlayerFacade<T = string> extends EventEmitter {
  public inited: boolean;
  private handleImplementationPlay: () => void;
  private handleImplementationPause: () => void;
  private handleImplementationTimeupdate: () => void;
  private handleImplementationError: (error) => boolean;
  private implementation: IPlayer;
  private __initPromise: Promise<void> | null;

  constructor({implementation}: {implementation: IPlayer}) {
    super();
    this.inited = false;
    this.handleImplementationPlay = this.emit.bind(this, 'playing');
    this.handleImplementationPause = this.emit.bind(this, 'pause');
    this.handleImplementationTimeupdate = this.emit.bind(this, 'timeupdate');
    this.handleImplementationError = (error) => this.emit('error', error);
    this.setImplementation(implementation);
  }

  get paused() {
    return this.implementation.paused;
  }

  get currentTime() {
    return this.implementation.currentTime;
  }

  get src() {
    return this.implementation.src;
  }

  set src(value) {
    this.implementation.src = value;
  }

  get currentTrackDuration() {
    return this.implementation.currentTrackDuration;
  }

  set currentTrackDuration(duration) {
    this.implementation.currentTrackDuration = duration;
  }

  init() {
    if (!this.__initPromise) {
      this.__initPromise = this.implementation
        .init()
        .then((res) => {
          this.inited = true;
          return res;
        })
        .catch((ex) => {
          this.inited = false;
          this.__initPromise = null;
          return Promise.reject(ex);
        });
    }
    return this.__initPromise;
  }

  setImplementation(implementation: IPlayer) {
    const wasInited = this.inited;
    if (this.implementation) {
      this._unsubscribeToEvents(this.implementation);
      this.inited = false;
      this.__initPromise = null;
    }
    this.implementation = implementation;
    this._subscribeToEvents(this.implementation);
    return wasInited ? this.init() : Promise.resolve();
  }

  play(what?: string) {
    if (what) {
      this.src = what;
    }

    this.implementation.play();
  }

  pause() {
    this.implementation.pause();
  }

  updateCurrentTime(value) {
    this.implementation.updateCurrentTime(value);
  }

  _subscribeToEvents(implementation) {
    implementation.on('playing', this.handleImplementationPlay);
    implementation.on('pause', this.handleImplementationPause);
    implementation.on('timeupdate', this.handleImplementationTimeupdate);
    implementation.on('error', this.handleImplementationError);
  }

  _unsubscribeToEvents(implementation) {
    implementation.removeListener('playing', this.handleImplementationPlay);
    implementation.removeListener('pause', this.handleImplementationPause);
    implementation.removeListener('timeupdate', this.handleImplementationTimeupdate);
    implementation.removeListener('error', this.handleImplementationError);
  }
}
